import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { MailCheck, KeyRound, CheckCircle2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useLayout } from "@/context/LayoutContext";
import { Button, Card, Field, Input, Loading } from "@/components/kit";

function Frame({ children }) {
  const { layout } = useLayout();
  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--surface-alt)" }}>
      <Card className="w-full max-w-md flex flex-col gap-5 fade-in">
        {layout?.logo_url && <img src={layout.logo_url} alt="" className="h-12 max-w-[180px] object-contain" />}
        {children}
      </Card>
    </div>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const send = useMutation({ mutationFn: () => api("/auth/forgot-password", { method: "POST", body: { email } }) });
  if (send.isSuccess) {
    return (
      <Frame>
        <MailCheck size={30} style={{ color: "var(--brand)" }} />
        <div className="h2" data-testid="forgot-sent-title">Confira seu e-mail</div>
        <p className="caption" data-testid="forgot-sent-text">Se existir uma conta com <b>{email}</b>, enviamos um link para criar uma nova senha. Ele vale por 1 hora. Veja também a caixa de spam.</p>
        <Link to="/login" className="caption underline" data-testid="forgot-back-login">Voltar para o login</Link>
      </Frame>
    );
  }
  return (
    <Frame>
      <div><div className="h2">Esqueci minha senha</div><p className="caption mt-1">Informe o e-mail do seu login e enviaremos um link para criar uma nova senha.</p></div>
      <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); if (email.trim()) send.mutate(); }}>
        <Field label="E-mail"><Input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="voce@restaurante.com" data-testid="forgot-email-input" /></Field>
        {send.isError && <p className="text-sm font-semibold" style={{ color: "var(--danger)" }} data-testid="forgot-error">{send.error.message}</p>}
        <Button type="submit" icon={MailCheck} loading={send.isPending} disabled={!email.trim()} data-testid="forgot-submit-button">Enviar link</Button>
      </form>
      <Link to="/login" className="caption underline">Voltar para o login</Link>
    </Frame>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const check = useQuery({ queryKey: ["reset-check", token], queryFn: () => api(`/auth/reset-password/${encodeURIComponent(token)}`), enabled: !!token, retry: false });
  const save = useMutation({ mutationFn: () => api("/auth/reset-password", { method: "POST", body: { token, password: pw } }), onError: (e) => setErr(e.message) });
  if (token && check.isLoading) return <Loading />;
  if (!token || check.isError) {
    return (
      <Frame>
        <XCircle size={30} style={{ color: "var(--danger)" }} />
        <div className="h2" data-testid="reset-invalid-title">Link inválido ou expirado</div>
        <p className="caption">Os links de recuperação valem por 1 hora e só podem ser usados uma vez.</p>
        <Link to="/esqueci-senha" className="underline font-semibold" data-testid="reset-request-new">Pedir um novo link</Link>
      </Frame>
    );
  }
  if (save.isSuccess) {
    return (
      <Frame>
        <CheckCircle2 size={30} style={{ color: "var(--success)" }} />
        <div className="h2" data-testid="reset-done-title">Senha alterada</div>
        <p className="caption">Agora é só entrar com a nova senha.</p>
        <Button onClick={() => nav("/login", { replace: true })} data-testid="reset-go-login">Ir para o login</Button>
      </Frame>
    );
  }
  const submit = (e) => {
    e.preventDefault();
    if (pw.length < 6) return setErr("A senha deve ter ao menos 6 caracteres");
    if (pw !== pw2) return setErr("As senhas não conferem");
    setErr("");
    save.mutate();
  };
  return (
    <Frame>
      <div><div className="h2">Criar nova senha</div><p className="caption mt-1" data-testid="reset-email-hint">Conta {check.data.email}</p></div>
      <form className="flex flex-col gap-4" onSubmit={submit}>
        <Field label="Nova senha"><Input type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Mínimo 6 caracteres" data-testid="reset-password-input" /></Field>
        <Field label="Repita a nova senha"><Input type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="reset-password-confirm-input" /></Field>
        {err && <p className="text-sm font-semibold" style={{ color: "var(--danger)" }} data-testid="reset-error">{err}</p>}
        <Button type="submit" icon={KeyRound} loading={save.isPending} data-testid="reset-submit-button">Salvar nova senha</Button>
      </form>
    </Frame>
  );
}
